import { useCallback, useEffect, useRef, useState } from "react"

/** Marks the render time trace we append to an async failure's real stack. */
const DECLARED_AT_LABEL = "Effect declared at:"

export function useAsyncEffect(cb: (opts: { isAlive(): boolean }) => void, updateOn?: any[]) {
  const traceRef = useRef<string | undefined>()
  traceRef.current = new Error().stack

  const throwError = useAsyncError()

  useEffect(() => {
    const isAlive = { current: true }
    const fn = async () => {
      await Promise.resolve(cb({ isAlive: () => isAlive.current }))
    }
    fn().catch(e => {
      console.error(e)
      throwError(e, traceRef.current)
    })

    return () => {
      isAlive.current = false
    }
  }, updateOn)
}

/**
 * A rejection does not always carry an Error: the skeem server answers some
 * failures with a plain string, and anything that has been JSON encoded arrives
 * as an object. Reading `.message` off those gives `undefined`, which used to
 * reach the error boundary as a blank error and produce a 500 report with
 * nothing in it to act on.
 */
export function describeThrownValue(e: any): string {
  if (typeof e === "string") {
    return e
  }
  if (e instanceof Error && e.message) {
    return e.message
  }
  if (e && typeof e === "object") {
    // ErrorBox keeps a key alongside its message, and server errors often
    // arrive as `{ error: "..." }`
    const described = [e.message, e.error, e.key].find(
      part => typeof part === "string" && part !== "",
    )
    if (described) {
      return described
    }
    try {
      const json = JSON.stringify(e)
      if (json && json !== "{}") {
        return json
      }
    } catch {
      // circular or otherwise unserialisable, fall through to the type name
    }
  }
  return `Unknown error (${Object.prototype.toString.call(e)})`
}

/**
 * The async stack of a rejection rarely says which effect it came from, which
 * is why the render time trace is captured. Keep both: the real throw site
 * first, then the trace, rather than replacing one with the other.
 */
export function formatAsyncError(e: any, stackTrace?: string): Error {
  const formattedError = new Error(describeThrownValue(e))

  if (e instanceof Error) {
    if (e.name) {
      formattedError.name = e.name
    }
    ;(formattedError as any).cause = e
  }

  const originalStack = e instanceof Error && typeof e.stack === "string" ? e.stack : undefined
  const stack = [originalStack, stackTrace && `${DECLARED_AT_LABEL}\n${stackTrace}`]
    .filter(Boolean)
    .join("\n")

  if (stack) {
    formattedError.stack = stack
  }

  return formattedError
}

export const useAsyncError = () => {
  const [_, setError] = useState()
  return useCallback(
    (e, stackTrace?: any) => {
      setError(() => {
        const formattedError = formatAsyncError(e, stackTrace)
        console.error(formattedError)
        throw formattedError
      })
    },
    [setError],
  )
}
